import React from 'react';

import { Button, Paper } from '@mui/material';
import { useForm } from 'react-hook-form';
import { TitleWithDescription } from '@infoods/lifebase-ui';

import classes from './VerifyCodeCard.module.scss';
import { VerificationCodeInput } from '../../../../../shared/components/form-components/VerificationCodeInput/VerificationCodeInput';
import { VerifyCodeFormValues } from '../../../../interfaces/VerifyCodeFormValues';
import { RESPONSE_STATUSES } from '../../../../../shared/constants/RESPONSE_STATUSES';

type VerifyCodeCardProps = {
  formId: string;
  consumer: string;
  nextVerificationCodeCallEpoch: number | null;
  onResendCodeClick: () => Promise<void>;
  onChangeMethodClick: () => void;
  verifyCode: (code: string) => Promise<void>;
};

const VerifyCodeCard = ({
  formId,
  consumer,
  nextVerificationCodeCallEpoch,
  onResendCodeClick,
  onChangeMethodClick,
  verifyCode,
}: VerifyCodeCardProps) => {
  const { control, handleSubmit, setError, clearErrors } = useForm<VerifyCodeFormValues>({
    reValidateMode: 'onSubmit',
    shouldFocusError: false,
  });

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await verifyCode(data.verificationCode);
    } catch (error: any) {
      if (error.response?.status === RESPONSE_STATUSES.VERIFICATION_CODE_INCORRECT) {
        setError('verificationCode', {
          message: 'Make sure you are using the correct code, or try to resend a new one',
        });
      }
      // All other errors are already handled by the API interceptor
    }
  });

  return (
    <form id={formId} onSubmit={handleFormSubmit}>
      <Paper variant="roundedBox" className={classes.paper}>
        <div className={classes.contentContainer}>
          <TitleWithDescription
            title={
              <span className={classes.title}>
                Protecting your personal data is our top priority!
              </span>
            }
            description={`Please confirm your account by entering the authorization code send to ${consumer}`}
          />
          <VerificationCodeInput
            control={control}
            className={classes.verificationCodeInput}
            name="verificationCode"
            onFocus={clearErrors}
            onResendCodeClick={onResendCodeClick}
            nextVerificationCodeCallEpoch={nextVerificationCodeCallEpoch}
            rules={{
              required: 'Verification code is required',
            }}
          />
          <p>Didn&apos;t receive it? Resend code or choose another verification method</p>
          <Button
            className={classes.tryAnotherMethodBtn}
            variant="text"
            onClick={onChangeMethodClick}
          >
            Try Another Method
          </Button>
        </div>
      </Paper>
    </form>
  );
};

export { VerifyCodeCard };
